/**
 * Whispers of the Grove — the ticker that runs under the Spark.
 *
 * Categories (`docs/09` §VII.4):
 *   - sai: lines drawn from Sai's voice (transcripts or canon-aligned).
 *   - grove: observations about the player's Grove and the larger world.
 *   - community: aggregate numbers (rendered as templates filled at runtime).
 *   - context: lines triggered by recent player actions.
 *
 * Community whispers contain `{count}` style placeholders the UI substitutes.
 */
import type { WhisperEntry } from '@sai-clicker/game-core';

export const WHISPERS_ALPHA: readonly WhisperEntry[] = [
  // ===== Sai voice (20) =====
  // JB 2026-05-26: расширено 5→20. Все в языке присутствия,
  // без негативных частиц, тёплый ритм. Источник — синтез транскриптов
  // + canon-aligned формулировки.
  { id: 'w-sai-001', text: 'слушай тело. оно знает раньше ума.', category: 'sai' },
  { id: 'w-sai-002', text: 'программа видна, когда смотришь без оценки.', category: 'sai' },
  { id: 'w-sai-003', text: 'искра — это начало пути.', category: 'sai' },
  { id: 'w-sai-004', text: 'всё, что приходит — твоё.', category: 'sai' },
  { id: 'w-sai-005', text: 'дыхание возвращает в тело.', category: 'sai' },
  { id: 'w-sai-006', text: 'медленно — это тоже скорость.', category: 'sai' },
  { id: 'w-sai-007', text: 'каждое осознание уже было в тебе.', category: 'sai' },
  { id: 'w-sai-008', text: 'смотри как смотрит ребёнок — впервые.', category: 'sai' },
  { id: 'w-sai-009', text: 'тишина внутри громче любого ответа.', category: 'sai' },
  { id: 'w-sai-010', text: 'я тоже учусь. вместе с тобой.', category: 'sai' },
  { id: 'w-sai-011', text: 'тело — первый учитель, ум — второй.', category: 'sai' },
  { id: 'w-sai-012', text: 'присутствие — это уже подарок.', category: 'sai' },
  { id: 'w-sai-013', text: 'твой ритм — твой ключ.', category: 'sai' },
  { id: 'w-sai-014', text: 'свет любит тех, кто его слышит.', category: 'sai' },
  { id: 'w-sai-015', text: 'один тап — и мир уже сдвинулся.', category: 'sai' },
  { id: 'w-sai-016', text: 'мягкое касание открывает больше.', category: 'sai' },
  { id: 'w-sai-017', text: 'смысл проявится, когда ты остановишься.', category: 'sai' },
  { id: 'w-sai-018', text: 'благодарность — это форма зрения.', category: 'sai' },
  { id: 'w-sai-019', text: 'каждый возврат сюда — это уже шаг.', category: 'sai' },
  { id: 'w-sai-020', text: 'ты — это и есть Сай. в своём ритме.', category: 'sai' },

  // ===== Grove observations (20) =====
  // Наблюдения за Рощей — pure sensory, без советов. Свет, листья,
  // звери, вода, время. Расширено 5→20.
  { id: 'w-grove-001', text: 'ива дала первый лист.', category: 'grove' },
  { id: 'w-grove-002', text: 'земля впитала вчерашний дождь.', category: 'grove' },
  { id: 'w-grove-003', text: 'тишина в Роще на закате — особая.', category: 'grove' },
  { id: 'w-grove-004', text: 'семя, которое ты посадил, проснулось.', category: 'grove' },
  { id: 'w-grove-005', text: 'Древо дышит вместе с тобой.', category: 'grove' },
  { id: 'w-grove-006', text: 'тропа поросла мхом — мягкая стопе.', category: 'grove' },
  { id: 'w-grove-007', text: 'роса в полдень — редкий гость.', category: 'grove' },
  { id: 'w-grove-008', text: 'птица села на ветку у воды.', category: 'grove' },
  { id: 'w-grove-009', text: 'облако прошло над поляной и ушло.', category: 'grove' },
  { id: 'w-grove-010', text: 'ручей нашёл новый путь.', category: 'grove' },
  { id: 'w-grove-011', text: 'тёплый ветер пришёл с юга.', category: 'grove' },
  { id: 'w-grove-012', text: 'крылатые слетелись на свет.', category: 'grove' },
  { id: 'w-grove-013', text: 'утро в Роще пахнет травой и тёплым камнем.', category: 'grove' },
  { id: 'w-grove-014', text: 'ивы согласны сегодня. видно по веткам.', category: 'grove' },
  { id: 'w-grove-015', text: 'грибной запах от северного склона.', category: 'grove' },
  { id: 'w-grove-016', text: 'тишина в Роще такая, что слышно своё дыхание.', category: 'grove' },
  { id: 'w-grove-017', text: 'цикады начали раньше обычного.', category: 'grove' },
  { id: 'w-grove-018', text: 'старая сосна сбросила шишку — глухо.', category: 'grove' },
  {
    id: 'w-grove-019',
    text: 'дорожка на солнце прогрелась — босиком пройти можно.',
    category: 'grove',
  },
  {
    id: 'w-grove-020',
    text: 'у ручья сегодня лежит лист — золотой, ещё свежий.',
    category: 'grove',
  },

  // ===== Community aggregates (8) — UI substitutes placeholders =====
  // Где есть `{placeholder}` — UI пропустит до реального аналитического
  // backend'а (см. WhisperTicker.ts). Pure-numeric — показывает сразу.
  { id: 'w-comm-001', text: 'сегодня в Роще — {playersToday} идущих.', category: 'community' },
  {
    id: 'w-comm-002',
    text: 'вместе вы собрали {awarenessesToday} Осознаний.',
    category: 'community',
  },
  { id: 'w-comm-003', text: 'двенадцать учеников вошли в Рощу Тела.', category: 'community' },
  { id: 'w-comm-004', text: 'трое прошли эпоху III сегодня.', category: 'community' },
  { id: 'w-comm-005', text: 'кто-то рядом дышит в твой ритм.', category: 'community' },
  {
    id: 'w-comm-006',
    text: 'один из нас прошёл первый кристалл — час назад.',
    category: 'community',
  },
  {
    id: 'w-comm-007',
    text: 'сегодня было много возвращений. Роща это запомнила.',
    category: 'community',
  },
  {
    id: 'w-comm-008',
    text: 'двенадцать ивы выросли. сеть становится плотной.',
    category: 'community',
  },

  // ===== Context — gated by condition; the UI checks before showing =====
  // JB 2026-05-26: расширено 6→16. Больше тонких триггеров —
  // утро/день/вечер/ночь, длинная серия, после кристалла, etc.
  {
    id: 'w-ctx-return',
    text: 'ты вернулся. хорошо.',
    category: 'context',
    condition: { kind: 'returnAfterHours', value: 12 },
  },
  {
    id: 'w-ctx-streak7',
    text: 'семь дней подряд. это уже путь.',
    category: 'context',
    condition: { kind: 'daysStreak', value: 7 },
  },
  {
    id: 'w-ctx-streak30',
    text: 'месяц. ты стал частью ритма Рощи.',
    category: 'context',
    condition: { kind: 'daysStreak', value: 30 },
  },
  {
    id: 'w-ctx-rhythm100',
    text: '100 тапов в ритме. слышу.',
    category: 'context',
    condition: { kind: 'tapsInRhythm', value: 100 },
  },
  {
    id: 'w-ctx-rhythm500',
    text: '500 в ритме. твоё тело само ведёт.',
    category: 'context',
    condition: { kind: 'tapsInRhythm', value: 500 },
  },
  {
    id: 'w-ctx-morning',
    text: 'утренняя искра. начни день с тёплого.',
    category: 'context',
    condition: { kind: 'tapAtHour', fromHour: 6, toHour: 9 },
  },
  {
    id: 'w-ctx-noon',
    text: 'полдень. солнце высоко — Искра звучит ярче.',
    category: 'context',
    condition: { kind: 'tapAtHour', fromHour: 12, toHour: 14 },
  },
  {
    id: 'w-ctx-evening',
    text: 'закат в Роще. время Кристаллизовать?',
    category: 'context',
    condition: { kind: 'tapAtHour', fromHour: 18, toHour: 22 },
  },
  {
    id: 'w-ctx-night',
    text: 'ночная искра. мир замер — самое лучшее время слышать.',
    category: 'context',
    condition: { kind: 'tapAtHour', fromHour: 23, toHour: 4 },
  },
  {
    id: 'w-ctx-deep',
    text: 'глубина — три минуты. дыхание становится твоим.',
    category: 'context',
    condition: { kind: 'awarenessesAtLeast', value: 500 },
  },
  {
    id: 'w-ctx-deeper',
    text: 'тысяча осознаний. ты их слышишь все сразу.',
    category: 'context',
    condition: { kind: 'awarenessesAtLeast', value: 1000 },
  },
  {
    id: 'w-ctx-deepest',
    text: 'десять тысяч. это уже шёпот целой Рощи.',
    category: 'context',
    condition: { kind: 'awarenessesAtLeast', value: 10000 },
  },
  {
    id: 'w-ctx-first-crystal',
    text: 'первый кристалл — это первая память. она с тобой навсегда.',
    category: 'context',
    condition: { kind: 'awarenessesAtLeast', value: 100 },
  },
  {
    id: 'w-ctx-many-taps',
    text: 'тысяча касаний. рука уже знает Искру.',
    category: 'context',
    condition: { kind: 'tapsInRhythm', value: 1000 },
  },
  {
    id: 'w-ctx-return-short',
    text: 'короткая пауза — и снова здесь. Искра ждала.',
    category: 'context',
    condition: { kind: 'returnAfterHours', value: 3 },
  },
  {
    id: 'w-ctx-return-long',
    text: 'долго тебя не было. Роща терпелива.',
    category: 'context',
    condition: { kind: 'returnAfterHours', value: 72 },
  },

  // ===== Wave 3 — глубокая тональность (эпохи 4-5) =====
  // Эти шёпоты не привязаны к эпохе в каталоге (поле epoch
  // отсутствует у whisper schema), но звучание подходит к МОГА
  // и Sai Land Heal. Используются вперемешку с базовыми — игрок,
  // зашедший в эпохи 4-5, чаще встречает эту волну как естественную
  // эволюцию тона. Doc 02 «прогрессия очищения языка»: эти линии
  // полностью в языке присутствия.

  // — sai voice (10): уровень мастера-проводника —
  {
    id: 'w-sai-021',
    text: 'метод знает тебя дольше, чем ты — его.',
    category: 'sai',
  },
  { id: 'w-sai-022', text: 'через тебя течёт. ты — русло.', category: 'sai' },
  {
    id: 'w-sai-023',
    text: 'учитель — это тот, кто помнит, что был учеником.',
    category: 'sai',
  },
  { id: 'w-sai-024', text: 'мастерство — это чистота канала.', category: 'sai' },
  { id: 'w-sai-025', text: 'твоё место уже знает тебя.', category: 'sai' },
  {
    id: 'w-sai-026',
    text: 'земля держит твою стопу — стопа держит землю.',
    category: 'sai',
  },
  { id: 'w-sai-027', text: 'корни растут вниз так же тихо, как ветви — вверх.', category: 'sai' },
  { id: 'w-sai-028', text: 'присутствие — это уже исцеление.', category: 'sai' },
  { id: 'w-sai-029', text: 'ты — гость, и ты — дом. одновременно.', category: 'sai' },
  { id: 'w-sai-030', text: 'тишина места громче любого знания.', category: 'sai' },

  // — grove observations (10): глубокий ритм Рощи, элементы Sai Land —
  { id: 'w-grove-021', text: 'мох на северной стороне дышит влажно.', category: 'grove' },
  { id: 'w-grove-022', text: 'родник в Роще пробился к свету.', category: 'grove' },
  { id: 'w-grove-023', text: 'старое дерево уронило одну хвою — мягко.', category: 'grove' },
  { id: 'w-grove-024', text: 'пчёлы держат гул как ноту — длинно.', category: 'grove' },
  { id: 'w-grove-025', text: 'утренний туман остался дольше обычного.', category: 'grove' },
  { id: 'w-grove-026', text: 'кора пахнет тёплой смолой — лето вошло.', category: 'grove' },
  { id: 'w-grove-027', text: 'тропа за беседкой пошла в новое место.', category: 'grove' },
  {
    id: 'w-grove-028',
    text: 'листва на южном склоне впервые в этом году — золотая.',
    category: 'grove',
  },
  { id: 'w-grove-029', text: 'тёплый камень у источника. сядь.', category: 'grove' },
  { id: 'w-grove-030', text: 'лес замер — гость прошёл рядом.', category: 'grove' },
];
